import { useCallback } from 'react';
import { App } from 'antd';
import { skuAPI } from '../../../../services/api';
import type { SKU } from '../../../../types';

/**
 * 获取字段的中文显示名称
 */
const getFieldDisplayName = (field: string): string => {
    const fieldMap: Record<string, string> = {
        name: 'SKU名称',
        name_cn: '中文名称',
        name_en: '英文名称',
        asin: 'ASIN',
        brand_obj: '关联品牌',
        brand: '品牌名称',
        category: '类目',
        description: '描述',
        price: '售价',
        profit_margin: '定价利润率',
        target_market: '目标市场',
        key_selling_points: '核心卖点',
        product_name: '产品全称',
        competitor_titles: '竞品标题',
        selling_points: '核心卖点列表',
        brand_story: '品牌故事',
        desired_feeling: '期望传达的感受',
        lifestyle_scene: '生活场景描述',
        site_keywords: '站点关键词配置',
        selling_points_fabe: '卖点FABE分析',
    };
    return fieldMap[field] || field;
};

export const useSKUSubmit = (onSuccess: () => void) => {
    const { message } = App.useApp();

    const handleSubmit = useCallback(async (values: any, editingSKU: SKU | null) => {
        try {
            // 清理数据
            const cleanedValues = { ...values };
            if (cleanedValues.brand_obj) {
                delete cleanedValues.brand;
            } else if (cleanedValues.brand) {
                delete cleanedValues.brand_obj;
            }

            if (cleanedValues.selling_points && Array.isArray(cleanedValues.selling_points)) {
                cleanedValues.selling_points = cleanedValues.selling_points
                    .filter((point: string) => point && point.trim().length > 0);
            }

            if (cleanedValues.site_keywords) {
                const siteKeywordsArray: Array<{ site: string; keywords: string }> = [];
                Object.entries(cleanedValues.site_keywords).forEach(([site, data]: [string, any]) => {
                    if (data && data.keywords) {
                        siteKeywordsArray.push({
                            site,
                            keywords: data.keywords || '',
                        });
                    }
                });
                cleanedValues.site_keywords = siteKeywordsArray;
            }

            const stringFields = ['name_en', 'name_cn', 'product_name', 'brand', 'category', 'description',
                'competitor_titles', 'brand_story', 'desired_feeling', 'lifestyle_scene',
                'target_market', 'key_selling_points'];
            stringFields.forEach(field => {
                if (field in cleanedValues && (cleanedValues[field] === null || cleanedValues[field] === undefined)) {
                    cleanedValues[field] = '';
                }
            });

            if (editingSKU) {
                await skuAPI.update(editingSKU.id, cleanedValues);
                message.success('更新成功');
            } else {
                await skuAPI.create(cleanedValues);
                message.success('创建成功');
            }

            onSuccess();
            return true;
        } catch (error: any) {
            const responseData = error?.response?.data;
            console.error('SKU保存错误完整响应:', JSON.stringify(responseData, null, 2));

            const parseValidationErrors = (data: any): string[] => {
                const errorMessages: string[] = [];
                if (data && typeof data === 'object' && !Array.isArray(data)) {
                    Object.entries(data).forEach(([field, errors]: [string, any]) => {
                        if (field === 'error' || field === 'message') return;

                        if (Array.isArray(errors) && errors.length > 0) {
                            const fieldName = getFieldDisplayName(field);
                            errorMessages.push(`${fieldName}: ${errors[0]}`);
                        } else if (typeof errors === 'string') {
                            const fieldName = getFieldDisplayName(field);
                            errorMessages.push(`${fieldName}: ${errors}`);
                        } else if (errors && typeof errors === 'object') {
                            const fieldName = getFieldDisplayName(field);
                            const nestedErrors = parseValidationErrors(errors);
                            if (nestedErrors.length > 0) {
                                errorMessages.push(`${fieldName}: ${nestedErrors[0]}`);
                            }
                        }
                    });
                }
                return errorMessages;
            };

            if (responseData?.details && typeof responseData.details === 'object') {
                const nestedValidationErrors = parseValidationErrors(responseData.details);
                if (nestedValidationErrors.length > 0) {
                    message.error(`保存失败: ${nestedValidationErrors[0]}`);
                    return false;
                }
            }

            const directValidationErrors = parseValidationErrors(responseData);
            if (directValidationErrors.length > 0) {
                message.error(`保存失败: ${directValidationErrors[0]}`);
                return false;
            }

            const errorMessage = responseData?.error || responseData?.message || error?.message || '保存失败';
            message.error(`保存失败: ${errorMessage}`);
            return false;
        }
    }, [onSuccess, message]);

    return { handleSubmit };
};
